"use client";

import "@/app/admin/admin.css";

import { useEffect, useMemo, useState } from "react";
import { CalendarDays } from "lucide-react";
import { useTranslations } from "next-intl";
import { Popover } from "@base-ui/react/popover";
import type { DateRange, Matcher } from "react-day-picker";

import { Calendar } from "@/components/ui/calendar";
import { SimpleSelect } from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  siteNowAsWallClockDate,
  siteWallClock,
} from "@/lib/datetime/display";
import {
  applyDateMask,
  dateFieldsOf,
  DEFAULT_DATE_FORMAT,
  formatByPattern,
  hasTimeFields,
  mergeDateTime,
  parseByPattern,
  tokenizeDateFormat,
} from "@/lib/datetime/pattern";
import { ANCHOR_HIDDEN_CLASS, cn } from "@/lib/utils";

export type DatePickerMode = "single" | "range";

const RANGE_SEP = " ~ ";

function parseBound(value: string | undefined, segs: ReturnType<typeof tokenizeDateFormat>) {
  if (!value) return undefined;
  const byFormat = parseByPattern(value, segs);
  if (byFormat) return byFormat;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;

  const parts = siteWallClock(date);
  return new Date(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
}

function startOfDay(date: Date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

type Shortcut =
  | { label: string; single: () => Date }
  | { label: string; range: () => DateRange };

type UiText = ReturnType<typeof useTranslations<"site.ui">>;

function singleShortcuts(t: UiText): Shortcut[] {
  return [
    { label: t("today"), single: () => siteNowAsWallClockDate() },
    {
      label: t("yesterday"),
      single: () => {
        const d = siteNowAsWallClockDate();
        d.setDate(d.getDate() - 1);
        return d;
      },
    },
    {
      label: t("tomorrow"),
      single: () => {
        const d = siteNowAsWallClockDate();
        d.setDate(d.getDate() + 1);
        return d;
      },
    },
  ];
}

function rangeShortcuts(t: UiText): Shortcut[] {
  const today = () => startOfDay(siteNowAsWallClockDate());
  return [
    { label: t("today"), range: () => ({ from: today(), to: today() }) },
    {
      label: t("last7Days"),
      range: () => {
        const to = today();
        const from = new Date(to);
        from.setDate(from.getDate() - 6);
        return { from, to };
      },
    },
    {
      label: t("last30Days"),
      range: () => {
        const to = today();
        const from = new Date(to);
        from.setDate(from.getDate() - 29);
        return { from, to };
      },
    },
    {
      label: t("thisMonth"),
      range: () => {
        const now = siteNowAsWallClockDate();
        return {
          from: new Date(now.getFullYear(), now.getMonth(), 1),
          to: new Date(now.getFullYear(), now.getMonth() + 1, 0),
        };
      },
    },
    {
      label: t("lastMonth"),
      range: () => {
        const now = siteNowAsWallClockDate();
        return {
          from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
          to: new Date(now.getFullYear(), now.getMonth(), 0),
        };
      },
    },
  ];
}

function TimeUnitSelect({
  value,
  count,
  disabled,
  onChange,
}: {
  value: number;
  count: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <SimpleSelect
      size="sm"
      className="px-2"
      contentClassName="min-w-[3.5rem]"
      disabled={disabled}
      value={String(value)}
      onValueChange={(next) => onChange(Number(next))}
      options={Array.from({ length: count }, (_, i) => ({
        value: String(i),
        label: String(i).padStart(2, "0"),
      }))}
    />
  );
}

export function DatePicker({
  value,
  format = DEFAULT_DATE_FORMAT,
  onChange,
  mode = "single",
  staticMode = false,
  shortcuts = false,
  min,
  max,
  disabled,
  className,
  id,
  placeholder,
}: {
  value: string;

  format?: string;
  onChange: (value: string) => void;
  mode?: DatePickerMode;

  staticMode?: boolean;

  shortcuts?: boolean;

  min?: string;
  max?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  placeholder?: string;
}) {
  const t = useTranslations("site.ui");
  const [open, setOpen] = useState(false);
  const [fallbackSingle, setFallbackSingle] = useState(() => siteNowAsWallClockDate());
  const [fallbackRange, setFallbackRange] = useState<DateRange>(() => {
    const today = startOfDay(siteNowAsWallClockDate());
    return { from: today, to: today };
  });
  const segs = useMemo(() => tokenizeDateFormat(format), [format]);
  const fields = useMemo(() => dateFieldsOf(segs), [segs]);
  const withTime = mode === "single" && hasTimeFields(fields);

  const minDate = useMemo(() => parseBound(min, segs), [min, segs]);
  const maxDate = useMemo(() => parseBound(max, segs), [max, segs]);

  const disabledMatchers = useMemo(() => {
    const list: Matcher[] = [];
    if (minDate) list.push({ before: minDate });
    if (maxDate) list.push({ after: maxDate });
    return list;
  }, [minDate, maxDate]);

  const now = siteNowAsWallClockDate();
  const startMonth = minDate ?? new Date(now.getFullYear() - 100, 0);
  const endMonth = maxDate ?? new Date(now.getFullYear() + 10, 11);

  const single = useMemo(
    () => (mode === "single" ? parseByPattern(value, segs) ?? undefined : undefined),
    [mode, value, segs],
  );
  const range = useMemo<DateRange | undefined>(() => {
    if (mode !== "range" || !value.trim()) return undefined;
    const [a, b] = value.split("~").map((part) => part.trim());
    const from = parseByPattern(a, segs) ?? undefined;
    const to = b ? parseByPattern(b, segs) ?? undefined : undefined;
    return from ? { from, to } : undefined;
  }, [mode, value, segs]);

  const panelActive = open || staticMode;

  useEffect(() => {
    if (!panelActive) return;
    if (mode === "single" && !single) {
      setFallbackSingle(siteNowAsWallClockDate());
    }
    if (mode === "range" && !range) {
      const today = startOfDay(siteNowAsWallClockDate());
      setFallbackRange({ from: today, to: today });
    }
  }, [panelActive, mode, single, range]);

  const effectiveSingle =
    single ?? (panelActive && mode === "single" ? fallbackSingle : undefined);
  const effectiveRange =
    range ?? (panelActive && mode === "range" ? fallbackRange : undefined);

  const emitSingle = (date: Date | undefined) => {
    onChange(date ? formatByPattern(date, segs) : "");
  };
  const emitRange = (next: DateRange | undefined) => {
    if (!next?.from) {
      onChange("");
      return;
    }
    const from = formatByPattern(next.from, segs);
    const to = next.to ? formatByPattern(next.to, segs) : "";
    onChange(to ? `${from}${RANGE_SEP}${to}` : from);
  };

  const timeBase = effectiveSingle ?? siteNowAsWallClockDate();

  const handleClear = () => {
    if (mode === "range") emitRange(undefined);
    else emitSingle(undefined);
  };
  const handleNow = () => {
    const nowDate = siteNowAsWallClockDate();
    if (mode === "range") emitRange({ from: startOfDay(nowDate), to: startOfDay(nowDate) });
    else emitSingle(nowDate);
    if (!staticMode) setOpen(false);
  };

  const footer = (
    <div className="flex items-center justify-between border-t border-border/60 px-3 py-2">
      <button
        type="button"
        className="rounded-md px-2.5 py-1 text-xs text-foreground/80 hover:bg-accent hover:text-foreground"
        onClick={handleClear}
      >
        {t("clear")}
      </button>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="rounded-md px-2.5 py-1 text-xs text-foreground/80 hover:bg-accent hover:text-foreground"
          onClick={handleNow}
        >
          {t("now")}
        </button>
        {!staticMode ? (
          <button
            type="button"
            className="rounded-md bg-primary px-3 py-1 text-xs text-primary-foreground hover:bg-primary/90"
            onClick={() => {
              if (mode === "single" && !value.trim() && effectiveSingle) {
                emitSingle(effectiveSingle);
              } else if (mode === "range" && !value.trim() && effectiveRange?.from) {
                emitRange(effectiveRange);
              }
              setOpen(false);
            }}
          >
            {t("confirm")}
          </button>
        ) : null}
      </div>
    </div>
  );

  const panel = (
    <div className="flex flex-col">
      <div className="flex gap-2">
      {shortcuts ? (
        <div className="flex w-20 shrink-0 flex-col gap-1 border-r border-border/60 p-2">
          {(mode === "range" ? rangeShortcuts(t) : singleShortcuts(t)).map((item) => (
            <button
              key={item.label}
              type="button"
              className="rounded-md px-2 py-1 text-left text-xs text-foreground/80 hover:bg-accent hover:text-foreground"
              onClick={() => {
                if ("single" in item) {
                  const picked = withTime
                    ? mergeDateTime(item.single(), {
                        hour: timeBase.getHours(),
                        minute: timeBase.getMinutes(),
                        second: timeBase.getSeconds(),
                      })
                    : item.single();
                  emitSingle(picked);
                  if (!staticMode) setOpen(false);
                } else {
                  emitRange(item.range());
                  if (!staticMode) setOpen(false);
                }
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}

      <div>
        {mode === "range" ? (
          <Calendar
            mode="range"
            selected={effectiveRange}
            onSelect={(next) => {
              emitRange(next);
              if (!staticMode && next?.from && next?.to) setOpen(false);
            }}
            numberOfMonths={2}
            captionLayout="dropdown"

            today={now}
            startMonth={startMonth}
            endMonth={endMonth}
            defaultMonth={effectiveRange?.from ?? minDate ?? now}
            disabled={disabledMatchers}
          />
        ) : (
          <Calendar
            mode="single"
            selected={effectiveSingle}
            onSelect={(next) => {
              if (!next) {
                emitSingle(undefined);
                return;
              }
              const merged = withTime
                ? mergeDateTime(next, {
                    hour: timeBase.getHours(),
                    minute: timeBase.getMinutes(),
                    second: timeBase.getSeconds(),
                  })
                : next;
              emitSingle(merged);
              if (!staticMode && !withTime) setOpen(false);
            }}
            captionLayout="dropdown"
            today={now}
            startMonth={startMonth}
            endMonth={endMonth}
            defaultMonth={effectiveSingle ?? minDate ?? now}
            disabled={disabledMatchers}
          />
        )}

        {withTime ? (
          <div className="flex items-center gap-1.5 border-t border-border/60 px-3 py-2">
            <span className="mr-1 text-xs text-muted-foreground">{t("time")}</span>
            <TimeUnitSelect
              value={timeBase.getHours()}
              count={24}
              onChange={(h) => emitSingle(mergeDateTime(effectiveSingle ?? siteNowAsWallClockDate(), { hour: h }))}
            />
            <span className="text-muted-foreground">:</span>
            <TimeUnitSelect
              value={timeBase.getMinutes()}
              count={60}
              onChange={(m) =>
                emitSingle(mergeDateTime(effectiveSingle ?? siteNowAsWallClockDate(), { minute: m }))
              }
            />
            {fields.second ? (
              <>
                <span className="text-muted-foreground">:</span>
                <TimeUnitSelect
                  value={timeBase.getSeconds()}
                  count={60}
                  onChange={(s) =>
                    emitSingle(mergeDateTime(effectiveSingle ?? siteNowAsWallClockDate(), { second: s }))
                  }
                />
              </>
            ) : null}
          </div>
        ) : null}
      </div>
      </div>
      {footer}
    </div>
  );

  if (staticMode) {
    return (
      <div
        className={cn(
          "w-fit rounded-lg border border-input bg-transparent dark:bg-input/30",
          disabled && "pointer-events-none opacity-50",
          className,
        )}
      >
        {panel}
      </div>
    );
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <div
        className={cn(
          "relative flex h-8 w-full max-w-72 items-center overflow-hidden rounded-lg border border-input bg-transparent transition-colors focus-within:border-[#333] dark:bg-input/30",
          disabled && "pointer-events-none opacity-50",
          className,
        )}
      >
        <input
          autoComplete="off"
          id={id}
          value={value}
          placeholder={placeholder ?? (mode === "range" ? `${format}${RANGE_SEP}${format}` : format)}
          disabled={disabled}
          readOnly={mode === "range"}
          spellCheck={false}
          inputMode={mode === "range" ? undefined : "numeric"}
          onChange={(event) => {
            if (mode === "range") return;
            onChange(applyDateMask(event.target.value, segs));
          }}
          onBlur={() => {
            if (mode === "range") return;
            const date = parseByPattern(value, segs);
            if (date) onChange(formatByPattern(date, segs));
          }}
          onClick={() => mode === "range" && setOpen(true)}
          className="h-full min-w-0 flex-1 bg-transparent px-2.5 py-1 text-base outline-none placeholder:text-muted-foreground md:text-sm"
        />
        <Tooltip>
          <TooltipTrigger
            render={
              <Popover.Trigger
                disabled={disabled}
                render={
                  <button
                    type="button"
                    aria-label={t("pickDate")}
                    className="flex h-full w-8 shrink-0 items-center justify-center border-l border-input text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <CalendarDays className="size-4" />
                  </button>
                }
              />
            }
          />
          <TooltipContent>{t("pickDate")}</TooltipContent>
        </Tooltip>
      </div>
      <Popover.Portal>
        <Popover.Positioner
          side="bottom"
          align="end"
          sideOffset={4}
          className={cn("z-50", ANCHOR_HIDDEN_CLASS)}
        >
          <Popover.Popup
            data-admin-dialog
            className="z-50 overflow-hidden rounded-lg bg-popover text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-none"
          >
            {panel}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
